import { Injectable, NgZone, inject, signal, computed, effect } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { DialogUtilsService } from '../modules/utils/dialog-utility.service';
import { IframeSecurityService } from './iframeSecurity.service';
import { DialogComponentMapperService } from './dialog-component-mapper.service';

/**
 * Tipos de diálogos soportados por el sistema
 */
export type DialogType =
  | 'login'
  | 'register'
  | 'otp'
  | 'emailVerify'
  | 'consent'
  | 'diffCountry'
  | 'blockedUser'
  | 'blockedMedical'
  | 'otpSuggest'
  | 'otpChangePassword'
  | 'userExists'
  | 'wrongUuid'
  | 'unauthorized';

/**
 * Modo de ejecución de la aplicación
 */
export type ExecutionMode = 'standalone' | 'iframe';

/**
 * Eventos que se notifican al parent (solo en modo iframe)
 */
export type ParentEventType =
  | 'opened'
  | 'closed'
  | 'changed'
  | 'navigating'
  | 'success'
  | 'error';

/**
 * Mensaje estructurado para comunicación con parent window
 */
export interface ParentMessage {
  type: string;
  dialogType?: DialogType;
  data?: any;
  timestamp: number;
  action?: string;
}

/**
 * Configuración para abrir un diálogo
 */
export interface DialogConfig {
  data?: any;
  width?: string;
  height?: string;
  maxWidth?: string;
  maxHeight?: string;
  disableClose?: boolean;
  panelClass?: string | string[];
  autoFocus?: boolean;
  enterAnimationDuration?: string;
  exitAnimationDuration?: string;
}

/**
 * Información del diálogo actual
 */
interface DialogInfo {
  type: DialogType;
  ref: MatDialogRef<any>;
  data?: any;
  openedAt: number;
}

/**
 * Servicio centralizado para gestionar la navegación entre diálogos
 * y la comunicación con el parent window en modo iframe.
 * 
 * Este servicio unifica:
 * - Apertura/cierre de diálogos
 * - Transiciones entre diálogos
 * - Comunicación con parent (iframe)
 * - Detección de modo de ejecución
 * - Gestión de tamaños de diálogo
 * 
 * @version Angular 19+ con inject() y signals
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationFlowService {
  
  // Inyección de dependencias con inject() - Angular 19+
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly dialogUtilsService = inject(DialogUtilsService);
  private readonly iframeSecurityService = inject(IframeSecurityService);
  private readonly dialogMapper = inject(DialogComponentMapperService);
  
  // Estado del servicio usando signals - Angular 19+
  private readonly _executionMode: ExecutionMode;
  private readonly _currentDialog = signal<DialogInfo | null>(null);
  private readonly _dialogStack = signal<DialogInfo[]>([]);
  private readonly _isInIframeDialog = signal(false);
  private readonly _isTransitioning = signal(false);
  
  // Computed signals para acceso reactivo
  public readonly currentDialog = this._currentDialog.asReadonly();
  public readonly dialogStack = this._dialogStack.asReadonly();
  public readonly isInIframeDialogSignal = this._isInIframeDialog.asReadonly();
  public readonly hasOpenDialogsSignal = computed(() => this._dialogStack().length > 0);
  
  // Subject para notificar cambios de estado (mantener para compatibilidad con RxJS)
  private readonly _dialogStateChange$ = new Subject<{
    action: 'opened' | 'closed' | 'changed';
    dialog: DialogInfo | null;
  }>();

  /**
   * Observable para suscribirse a cambios de estado de diálogos
   */
  public readonly dialogStateChange$ = this._dialogStateChange$.asObservable();

  constructor() {
    this._executionMode = this.detectExecutionMode();
    this.setupDialogTracking();
    
    console.log('[NavigationFlow] Service initialized in mode:', this._executionMode);
    
    // Effect para logging de cambios de estado (Angular 19+)
    effect(() => {
      const current = this._currentDialog();
      if (current) {
        console.log('[NavigationFlow] Current dialog changed:', current.type);
      }
    });
  }

  // ============================================
  // DETECCIÓN DE CONTEXTO
  // ============================================

  /**
   * Detecta el modo de ejecución de la aplicación
   */
  private detectExecutionMode(): ExecutionMode {
    try {
      return window.self !== window.top ? 'iframe' : 'standalone';
    } catch (e) {
      // Si hay error de seguridad, definitivamente estamos en iframe
      return 'iframe';
    }
  }

  /**
   * Verifica si está en modo iframe
   */
  public isIframeMode(): boolean {
    return this._executionMode === 'iframe';
  }

  /**
   * Verifica si está en modo standalone
   */
  public isStandaloneMode(): boolean {
    return this._executionMode === 'standalone';
  }

  /**
   * Obtiene el modo de ejecución actual
   */
  public getExecutionMode(): ExecutionMode {
    return this._executionMode;
  }

  /**
   * Verifica si el digital asset está configurado como v2.0 (modo diálogo/iframe)
   * basándose en la sessionData almacenada
   */
  public isApiVersion2(): boolean {
    try {
      const sessionData = localStorage.getItem('ferrerSessionData');
      if (!sessionData) {
        return false;
      }
      const parsedData = JSON.parse(sessionData);
      return parsedData?.apiVersion === '2.0';
    } catch (error) {
      console.error('[NavigationFlow] Error checking API version:', error);
      return false;
    }
  }

  /**
   * Verifica si se debe abrir en modo diálogo
   * (Ya sea porque está en iframe O porque es apiVersion 2.0)
   */
  public shouldOpenAsDialog(): boolean {
    return this.isIframeMode() || this.isApiVersion2();
  }

  // ============================================
  // GESTIÓN DE DIÁLOGOS
  // ============================================

  /**
   * Abre un diálogo del tipo especificado
   * 
   * @param dialogType Tipo de diálogo a abrir
   * @param data Datos a pasar al diálogo
   * @param config Configuración adicional del diálogo
   * @returns Observable que emite el resultado del diálogo al cerrarse
   */
  public openDialog(
    dialogType: DialogType,
    data?: any,
    config?: Partial<DialogConfig>
  ): Observable<any> {
    return new Observable(observer => {
      this.ngZone.run(() => {
        try {
          // Verificar si ya hay un diálogo del mismo tipo abierto
          if (this._currentDialog()?.type === dialogType) {
            console.warn(`[NavigationFlow] Dialog of type '${dialogType}' is already open`);
            observer.complete();
            return;
          }

          // Obtener el componente del diálogo
          const component = this.getDialogComponent(dialogType);
          if (!component) {
            console.error(`[NavigationFlow] Unknown dialog type: ${dialogType}`);
            observer.error(new Error(`Unknown dialog type: ${dialogType}`));
            return;
          }

          // Preparar configuración del diálogo
          const dialogConfig = this.prepareDialogConfig(dialogType, data, config);
          
          console.log(`[NavigationFlow] Opening dialog: ${dialogType}`, {
            component: component.name || component,
            config: dialogConfig
          });

          // Abrir el diálogo
          const dialogRef = this.dialog.open(component, dialogConfig);
          
          console.log(`[NavigationFlow] Dialog opened successfully:`, {
            type: dialogType,
            dialogRef,
            id: dialogRef.id
          });

          // Registrar el diálogo actual usando signals
          const dialogInfo: DialogInfo = {
            type: dialogType,
            ref: dialogRef,
            data,
            openedAt: Date.now()
          };
          
          this._currentDialog.set(dialogInfo);

          // Agregar a la pila de diálogos usando signals
          this._dialogStack.update(stack => [...stack, dialogInfo]);

          // Marcar que estamos en modo diálogo (para iframe)
          if (this.isIframeMode()) {
            this._isInIframeDialog.set(true);
          }

          // Configurar listener de resize si es necesario
          const cleanupResize = this.dialogUtilsService.setupResponsiveDialog(dialogRef);

          // Notificar apertura
          this.notifyDialogOpened(dialogType, data);
          this._dialogStateChange$.next({
            action: 'opened',
            dialog: dialogInfo
          });

          // Manejar cierre del diálogo
          dialogRef.afterClosed().subscribe(result => {
            // Limpiar listener de resize
            cleanupResize();

            // Remover de la pila usando signals
            this._dialogStack.update(stack => stack.filter(d => d.ref !== dialogRef));

            // Si este era el diálogo actual, limpiarlo
            if (this._currentDialog()?.ref === dialogRef) {
              this._currentDialog.set(null);
            }

            // Si no hay más diálogos abiertos
            const currentStack = this._dialogStack();
            const isTransitioning = this._isTransitioning();
            
            if (currentStack.length === 0 && !isTransitioning) {
              // Solo notificar cierre si NO estamos en transición
              this._isInIframeDialog.set(false);
              this.notifyDialogClosed(dialogType, result);
              this._dialogStateChange$.next({
                action: 'closed',
                dialog: null
              });
            } else if (currentStack.length > 0) {
              // Actualizar el diálogo actual al último de la pila
              const lastDialog = currentStack[currentStack.length - 1];
              this._currentDialog.set(lastDialog);
              this.notifyDialogChanged(lastDialog.type);
              this._dialogStateChange$.next({
                action: 'changed',
                dialog: lastDialog
              });
            }
            // Si isTransitioning es true, no hacer nada - el nuevo diálogo se abrirá

            // Emitir resultado
            observer.next(result);
            observer.complete();
          });

        } catch (error) {
          console.error('[NavigationFlow] Error opening dialog:', error);
          observer.error(error);
        }
      });
    });
  }

  /**
   * Cierra el diálogo actual
   * 
   * @param result Resultado a retornar al cerrar
   */
  public closeCurrentDialog(result?: any): void {
    const current = this._currentDialog();
    if (current) {
      console.log(`[NavigationFlow] Closing current dialog: ${current.type}`);
      current.ref.close(result);
      
      // ✅ En v1.5 standalone, navegar al siteUrl después de cerrar
      const isInIframe = window.self !== window.top;
      
      if (!isInIframe) {
        let isV2 = false;
        try {
          const sessionData = localStorage.getItem('ferrerSessionData');
          if (sessionData) {
            const parsedData = JSON.parse(sessionData);
            isV2 = parsedData?.apiVersion === '2.0';
            
            // Si es v1.5 y tenemos siteUrl, navegar
            if (!isV2 && parsedData?.siteUrl) {
              console.log('[NavigationFlow] v1.5 standalone detected, navigating to siteUrl:', parsedData.siteUrl);
              setTimeout(() => {
                window.location.href = parsedData.siteUrl;
              }, 300);
            }
          }
        } catch (error) {
          console.error('[NavigationFlow] Error checking version for navigation:', error);
        }
      }
    } else {
      console.warn('[NavigationFlow] No current dialog to close');
    }
  }

  /**
   * Cierra todos los diálogos abiertos
   */
  public closeAllDialogs(): void {
    const stack = this._dialogStack();
    const matDialogCount = this.dialog.openDialogs.length;
    const totalDialogs = stack.length + matDialogCount;
    
    console.log(`[NavigationFlow] Closing all dialogs (${totalDialogs} total: ${stack.length} in stack, ${matDialogCount} in MatDialog)`);
    
    // Cerrar todos los diálogos en la pila de NavigationFlowService
    stack.forEach(dialogInfo => {
      console.log(`[NavigationFlow] Closing dialog from stack: ${dialogInfo.type}`);
      dialogInfo.ref.close();
    });

    // Cerrar cualquier diálogo gestionado directamente por MatDialog
    // Copiar el array para evitar problemas de mutación durante iteración
    const openDialogs = [...this.dialog.openDialogs];
    openDialogs.forEach((dialogRef, index) => {
      console.log(`[NavigationFlow] Closing dialog ${index + 1}/${openDialogs.length} from MatDialog.openDialogs`);
      try {
        dialogRef.close();
      } catch (error) {
        console.error(`[NavigationFlow] Error closing dialog ${index}:`, error);
      }
    });

    // Resetear estado usando signals
    this._dialogStack.set([]);
    this._currentDialog.set(null);
    this._isInIframeDialog.set(false);

    console.log(`[NavigationFlow] All close() calls completed (MatDialog may still be animating, count: ${this.dialog.openDialogs.length})`);
    
    // Notificar cierre completo
    this.notifyAllDialogsClosed();
  }

  /**
   * Realiza una transición desde el diálogo actual a otro diálogo
   * 
   * @param toDialogType Tipo de diálogo de destino
   * @param data Datos para el nuevo diálogo
   * @param closeResult Resultado al cerrar el diálogo actual
   * @returns Observable del nuevo diálogo
   */
  public transitionToDialog(
    toDialogType: DialogType,
    data?: any,
    closeResult?: any
  ): Observable<any> {
    console.log(`[NavigationFlow] Transitioning to dialog: ${toDialogType}`);
    
    // Marcar que estamos en transición para evitar notificar cierre al parent
    this._isTransitioning.set(true);
    
    // Cerrar el diálogo actual
    const current = this._currentDialog();
    if (current) {
      console.log(`[NavigationFlow] Closing current dialog: ${current.type}`);
      current.ref.close(closeResult);
    }

    // ✅ SPECIFIC FIX for login ↔ register transitions:
    // Close any login/register dialogs that might be stacked
    const stackedAuthDialogs = this._dialogStack().filter(d => 
      (d.type === 'login' || d.type === 'register') && d.ref.id !== current?.ref.id
    );
    
    if (stackedAuthDialogs.length > 0) {
      console.log(`[NavigationFlow] Found ${stackedAuthDialogs.length} stacked auth dialog(s), closing them`);
      stackedAuthDialogs.forEach((dialog, index) => {
        console.log(`[NavigationFlow] Closing stacked auth dialog ${index + 1}: ${dialog.type} (id: ${dialog.ref.id})`);
        try {
          dialog.ref.close(closeResult);
        } catch (error) {
          console.error(`[NavigationFlow] Error closing stacked auth dialog ${dialog.type}:`, error);
        }
      });
    }

    // ✅ SAFETY CHECK: Verificar si hay diálogos "huérfanos" no rastreados en MatDialog
    // Esto puede pasar si un diálogo se abrió sin usar NavigationFlowService
    const trackedDialogIds = new Set(this._dialogStack().map(d => d.ref.id));
    const orphanDialogs = this.dialog.openDialogs.filter(ref => !trackedDialogIds.has(ref.id));
    
    if (orphanDialogs.length > 0) {
      console.warn(`[NavigationFlow] Found ${orphanDialogs.length} orphan dialog(s) not tracked by NavigationFlowService`);
      orphanDialogs.forEach((dialogRef, index) => {
        console.log(`[NavigationFlow] Closing orphan dialog ${index + 1}/${orphanDialogs.length} (id: ${dialogRef.id})`);
        try {
          dialogRef.close(closeResult);
        } catch (error) {
          console.error(`[NavigationFlow] Error closing orphan dialog:`, error);
        }
      });
    }

    // Pequeño delay para asegurar que el diálogo anterior se cierre
    return new Observable(observer => {
      setTimeout(() => {
        this.openDialog(toDialogType, data).subscribe({
          next: result => {
            // Limpiar flag de transición
            this._isTransitioning.set(false);
            observer.next(result);
          },
          error: err => {
            // Limpiar flag de transición incluso en error
            this._isTransitioning.set(false);
            observer.error(err);
          },
          complete: () => {
            observer.complete();
          }
        });
      }, 100); // Mantener 100ms para asegurar cierre completo
    });
  }

  /**
   * Obtiene información del diálogo actual
   */
  public getCurrentDialog(): DialogInfo | null {
    return this._currentDialog();
  }

  /**
   * Verifica si hay algún diálogo abierto
   */
  public hasOpenDialogs(): boolean {
    return this._dialogStack().length > 0 || this.dialog.openDialogs.length > 0;
  }

  /**
   * Verifica si el modo iframe dialog está activo
   */
  public isInIframeDialog(): boolean {
    return this._isInIframeDialog();
  }

  /**
   * Establece manualmente el estado de iframe dialog
   * (Útil para compatibilidad con código existente)
   */
  public setIframeDialogState(state: boolean): void {
    this._isInIframeDialog.set(state);
  }

  // ============================================
  // COMUNICACIÓN CON PARENT (solo iframe)
  // ============================================

  /**
   * Notifica al parent window sobre un evento del diálogo
   * Solo funciona en modo iframe
   * 
   * @param eventType Tipo de evento
   * @param dialogType Tipo de diálogo
   * @param data Datos adicionales
   */
  private notifyParent(
    eventType: ParentEventType,
    dialogType?: DialogType,
    data?: any
  ): void {
    if (!this.isIframeMode()) {
      return; // No hacer nada si no estamos en iframe
    }

    const parentOrigin = this.iframeSecurityService.getParentOrigin();
    
    if (!window.parent || window.parent === window) {
      return;
    }

    try {
      // Mensaje estructurado
      const message: ParentMessage = {
        type: `dialog.${eventType}`,
        dialogType,
        data,
        timestamp: Date.now()
      };

      // Enviar mensaje estructurado
      if (parentOrigin) {
        window.parent.postMessage(message, parentOrigin);
      } else {
        window.parent.postMessage(message, '*');
      }

      // Mensajes legacy para compatibilidad con código existente
      this.sendLegacyMessages(eventType, dialogType);

      console.log(`[NavigationFlow] Notified parent: ${eventType}`, message);
    } catch (error) {
      console.error('[NavigationFlow] Error notifying parent:', error);
    }
  }

  /**
   * Envía mensajes legacy para compatibilidad con código existente
   */
  private sendLegacyMessages(eventType: ParentEventType, dialogType?: DialogType): void {
    if (!window.parent || window.parent === window) return;

    try {
      switch (eventType) {
        case 'opened':
          if (dialogType === 'login') {
            window.parent.postMessage('loginDialogOpened', '*');
          } else if (dialogType === 'register') {
            window.parent.postMessage('registerDialogOpened', '*');
          } else if (dialogType === 'otp') {
            window.parent.postMessage('rememberDialogOpened', '*');
          }
          break;

        case 'closed':
          window.parent.postMessage('closeDialog', '*');
          window.parent.postMessage('loginDialogClosed', '*');
          window.parent.postMessage('dialogClosed', '*');
          window.parent.postMessage('stopRetry', '*');
          break;

        case 'changed':
          window.parent.postMessage('dialogChanged', '*');
          break;

        case 'navigating':
          window.parent.postMessage('dialogNavigating', '*');
          break;
      }
    } catch (error) {
      console.error('[NavigationFlow] Error sending legacy messages:', error);
    }
  }

  /**
   * Notifica que un diálogo se ha abierto
   */
  private notifyDialogOpened(dialogType: DialogType, data?: any): void {
    this.notifyParent('opened', dialogType, data);
  }

  /**
   * Notifica que un diálogo se ha cerrado
   */
  private notifyDialogClosed(dialogType: DialogType, result?: any): void {
    this.notifyParent('closed', dialogType, result);
  }

  /**
   * Notifica que el diálogo actual ha cambiado
   */
  private notifyDialogChanged(dialogType: DialogType): void {
    this.notifyParent('changed', dialogType);
  }

  /**
   * Notifica que todos los diálogos se han cerrado
   */
  private notifyAllDialogsClosed(): void {
    this.notifyParent('closed');
  }

  /**
   * Notifica navegación dentro del diálogo
   */
  public notifyDialogNavigating(): void {
    this.notifyParent('navigating', this._currentDialog()?.type);
  }

  /**
   * Notifica éxito en autenticación
   */
  public notifyAuthSuccess(data?: any): void {
    this.notifyParent('success', this._currentDialog()?.type, data);
  }

  /**
   * Notifica error
   */
  public notifyError(error: any): void {
    this.notifyParent('error', this._currentDialog()?.type, error);
  }

  // ============================================
  // UTILIDADES
  // ============================================

  /**
   * Calcula el tamaño del diálogo según el tipo y el modo
   */
  private calculateDialogSize(dialogType: DialogType): { width: number; height: number } {
    // En modo iframe, usar tamaño fijo
    if (this.isIframeMode()) {
      return { width: 608, height: 552 };
    }

    // En modo standalone, usar cálculo responsive
    return this.dialogUtilsService.calculateDialogSize();
  }

  /**
   * Prepara la configuración del diálogo
   */
  private prepareDialogConfig(
    dialogType: DialogType,
    data?: any,
    customConfig?: Partial<DialogConfig>
  ): any {
    const size = this.calculateDialogSize(dialogType);
    
    // Configuración base con animaciones habilitadas
    const baseConfig: any = {
      width: `${size.width}px`,
      maxWidth: this.isIframeMode() ? '95vw' : '90vw',
      maxHeight: '88vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog-panel',
      enterAnimationDuration: '300ms',  // Animación de entrada suave
      exitAnimationDuration: '200ms',   // Animación de salida rápida
      data: data || {}
    };

    // Merge con configuración personalizada
    return { ...baseConfig, ...customConfig };
  }

  /**
   * Obtiene el componente del diálogo según el tipo
   */
  private getDialogComponent(dialogType: DialogType): any {
    return this.dialogMapper.getComponent(dialogType);
  }

  // ============================================
  // MÉTODOS HELPER ESPECÍFICOS POR TIPO DE DIÁLOGO
  // ============================================

  /**
   * Abre el diálogo de login
   */
  public openLoginDialog(data?: any): Observable<any> {
    return this.openDialog('login', data);
  }

  /**
   * Abre el diálogo de registro
   */
  public openRegisterDialog(data?: any): Observable<any> {
    return this.openDialog('register', data);
  }

  /**
   * Abre el diálogo de OTP (recuperar contraseña)
   */
  public openOtpDialog(email: string, authId?: string): Observable<any> {
    return this.openDialog('otp', { email, authId });
  }

  /**
   * Abre el diálogo de verificación de email
   */
  public openEmailVerifyDialog(email: string): Observable<any> {
    return this.openDialog('emailVerify', { email });
  }

  /**
   * Abre el diálogo de consentimientos
   */
  public openConsentDialog(data: {
    email: string;
    password?: string;
    otpCode?: string;
    loginaction: string;
    consentsAceptados: any;
    userCountry: string;
  }): Observable<any> {
    return this.openDialog('consent', data);
  }

  /**
   * Abre el diálogo de país diferente
   */
  public openDiffCountryDialog(data: {
    email: string;
    password?: string;
    otpCode?: string;
    loginaction: string;
    consentsAceptados: any;
    userCountry: string;
  }): Observable<any> {
    return this.openDialog('diffCountry', data);
  }

  /**
   * Abre el diálogo de usuario bloqueado
   */
  public openBlockedUserDialog(data: {
    email: string;
    password?: string;
    otpCode?: string;
    loginaction: string;
  }): Observable<any> {
    return this.openDialog('blockedUser', data);
  }

  /**
   * Abre el diálogo de HCP bloqueado (no médico)
   */
  public openBlockedMedicalDialog(data: {
    email: string;
    password?: string;
    otpCode?: string;
  }): Observable<any> {
    return this.openDialog('blockedMedical', data);
  }

  /**
   * Abre el diálogo de sugerencia de cambio de contraseña
   */
  public openOtpSuggestDialog(data: {
    apiResponse: any;
    email: string;
  }): Observable<any> {
    return this.openDialog('otpSuggest', data);
  }

  /**
   * Abre el diálogo de cambio de contraseña
   */
  public openOtpChangePasswordDialog(data: any): Observable<any> {
    return this.openDialog('otpChangePassword', data);
  }

  /**
   * Abre el diálogo de usuario existente
   */
  public openUserExistsDialog(data?: any): Observable<any> {
    return this.openDialog('userExists', data);
  }

  /**
   * Abre el diálogo de UUID incorrecto
   */
  public openWrongUuidDialog(data?: any): Observable<any> {
    return this.openDialog('wrongUuid', data);
  }

  // ============================================
  // TRANSICIONES COMUNES
  // ============================================

  /**
   * Transición de login a registro
   */
  public transitionLoginToRegister(): Observable<any> {
    return this.transitionToDialog('register');
  }

  /**
   * Transición de registro a login
   */
  public transitionRegisterToLogin(): Observable<any> {
    return this.transitionToDialog('login');
  }

  /**
   * Transición de login a OTP
   */
  public transitionLoginToOtp(email: string): Observable<any> {
    return this.transitionToDialog('otp', { email });
  }

  /**
   * Transición de OTP a login
   */
  public transitionOtpToLogin(): Observable<any> {
    return this.transitionToDialog('login');
  }

  /**
   * Transición a verificación de email
   */
  public transitionToEmailVerify(email: string): Observable<any> {
    return this.transitionToDialog('emailVerify', { email });
  }

  /**
   * Transición desde verificación de email a login
   */
  public transitionEmailVerifyToLogin(): Observable<any> {
    return this.transitionToDialog('login');
  }

  /**
   * Determina si se debe mostrar el botón de cerrar
   */
  public shouldShowCloseButton(): boolean {
    return this.dialogUtilsService.shouldShowCloseButton();
  }

  /**
   * Configura el tracking de diálogos del MatDialog
   */
  private setupDialogTracking(): void {
    // Suscribirse a afterAllClosed de MatDialog
    this.dialog.afterAllClosed.subscribe(() => {
      if (this._dialogStack().length === 0 && this._isInIframeDialog()) {
        this._isInIframeDialog.set(false);
        this.notifyAllDialogsClosed();
      }
    });
  }

  /**
   * Navega después de cerrar el diálogo
   * Solo aplica en modo standalone
   */
  public navigateAfterClose(callback?: string | (() => void)): void {
    if (this.isIframeMode()) {
      // En iframe, solo cerrar y notificar al parent
      this.closeAllDialogs();
      return;
    }

    // En standalone, manejar navegación
    if (typeof callback === 'function') {
      callback();
    } else if (typeof callback === 'string') {
      this.router.navigateByUrl(callback);
    } else {
      // Fallback: intentar navegar según callback guardado o referrer
      const savedCallback = localStorage.getItem('ferrer');
      if (savedCallback) {
        window.location.href = savedCallback;
      } else if (document.referrer) {
        window.location.href = document.referrer;
      } else if (window.history.length > 1) {
        window.history.back();
      }
    }
  }
}
